import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { Callout, Card, ListEmptyState, LoadingSpinner } from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import {
  CompanyExpensePayableLine,
  CompanyExpensePayables,
} from '../../types/company-expense';
import { formatDate, formatMoney } from '../../utils/format';

function PayableSection({
  title,
  totalGross,
  items,
}: {
  title: string;
  totalGross: string;
  items: CompanyExpensePayableLine[];
}) {
  const labels = t().companyExpenses;
  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        <div className="text-sm font-semibold tabular-nums text-ink">
          {formatMoney(totalGross)}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-ink-faint">—</p>
      ) : (
        <DataTablePanel>
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.dueDate}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.vendorName}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.category}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.type}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.remainingNet}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.remainingVat}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.remainingGross}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr
                  key={`${item.expenseId}-${item.kind}-${index}`}
                  className="border-t border-line"
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Link
                      to={`/company-expenses/${item.expenseId}/edit`}
                      className="text-brand hover:underline"
                    >
                      {formatDate(item.dueDate)}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    {item.vendorName || '—'}
                    {item.documentNo ? (
                      <div className="text-xs text-ink-faint">
                        {item.documentNo}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">{item.categoryName}</td>
                  <td className="px-3 py-2">
                    {item.kind === 'vat'
                      ? labels.kindVat
                      : item.kind === 'net'
                        ? labels.kindNet
                        : item.type === 'INVOICE'
                          ? t().financial.invoice
                          : t().financial.cash}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(item.remainingNet)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(item.remainingVat)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {formatMoney(item.remainingGross)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTablePanel>
      )}
    </Card>
  );
}

export function CompanyExpensePayablesPage() {
  const labels = t().companyExpenses;
  const [data, setData] = useState<CompanyExpensePayables | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getCompanyExpensePayables();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const empty =
    data &&
    data.overdue.items.length === 0 &&
    data.due7Days.items.length === 0 &&
    data.later.items.length === 0;

  return (
    <PageShell>
      <div className="shrink-0 px-1">
        <h1 className="text-lg font-semibold text-ink">{labels.payablesTitle}</h1>
      </div>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      {loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : empty ? (
        <ListEmptyState title={labels.emptyPayables} />
      ) : data ? (
        <div className="space-y-4">
          <PayableSection
            title={labels.overdue}
            totalGross={data.overdue.totalGross}
            items={data.overdue.items}
          />
          <PayableSection
            title={labels.due7Days}
            totalGross={data.due7Days.totalGross}
            items={data.due7Days.items}
          />
          <PayableSection
            title={labels.later}
            totalGross={data.later.totalGross}
            items={data.later.items}
          />
        </div>
      ) : null}
    </PageShell>
  );
}
